import type {
  CareItem,
  CareItemType,
} from 'src/replicant/buruburu/care/care.ruleset';
import {
  ButtonView,
  Color,
  MovieClip,
  TintFilter,
  View,
} from '@play-co/timestep-core/ui';
import { Care } from 'src/replicant/buruburu/care';
import { getCareItemAnimationName } from 'src/lib/ui/config/buruburu';
import { Pet, PetSpecies } from 'src/replicant/buruburu/pet';
import StateObserver from '../../StateObserver';
import { FloatAnimator } from '../../lib/Animator';
import uiConfig from '../../lib/ui/config';
import platform from '@play-co/gcinstant';
import { waitForAssets } from '../../lib/utils';
import { petEvolutionAssets } from '../../loadingGroups';
import sounds from '../../lib/sounds';

type EntityType = 'pet' | 'careItem';

export interface Entity extends View {
  moving: boolean;
  entityId: string;
  setFlipped(flipped: boolean): void;
}
export const isEntity = (view: View): view is Entity => 'entityId' in view;

export class PetEntity extends ButtonView implements Entity {
  static TapEvent = 'Tap';

  public get zIndex(): number {
    return this.style.zIndex;
  }
  public set zIndex(value: number) {
    this.updateOpts({
      zIndex: Math.floor(value),
    });
  }
  public moving = false;

  private petClip: MovieClip;
  private statusClip: MovieClip;
  private glowClip: MovieClip;
  private glowBackground: View;
  private glowEffectAnimator: FloatAnimator;

  constructor(public entityId: string, species: PetSpecies) {
    super({
      width: 400,
      height: 400,
      offsetX: -200,
      offsetY: -300,
      onClick: () => {
        sounds.playSound('tapPet');
        this.emit(PetEntity.TapEvent);
      },
    });

    this.statusClip = new MovieClip({
      superview: this,
      url: 'assets/status',
      scale: 0.88,
      offsetX: 200,
      offsetY: 300,
    });

    this.petClip = new MovieClip({
      superview: this,
      offsetX: 200,
      offsetY: 300,
    });
    this.setSpecies(species);

    this.glowClip = new MovieClip({
      superview: this,
      url: 'assets/care/evolve',
      offsetX: -270,
      offsetY: -320,
      scale: 0.88,
    });

    const filter = new TintFilter(new Color('rgba(255, 255, 255, 0)'));
    this.petClip.setFilter(filter);
    this.glowEffectAnimator = new FloatAnimator((value) => {
      this.glowBackground.style.opacity = value;
      filter.update(new Color(`rgba(255, 255, 255, ${value})`));
    });
  }

  setFlipped(flipped: boolean) {
    this.petClip.updateOpts({ flipX: flipped });
  }

  setSpecies(species: PetSpecies) {
    this.petClip.updateOpts(petEntityOpts[species]);
  }

  setAnimation(pet: string) {
    if (!this.petClip.isAnimationPlaying(pet)) {
      this.petClip.loop(pet);
    }
  }

  setStatusAnimation(status: string) {
    if (!status) {
      this.statusClip.hide();
    } else if (
      !this.statusClip.style.visible ||
      !this.statusClip.isAnimationPlaying(status)
    ) {
      this.statusClip.show();
      this.statusClip.loop(status);
    }
  }

  async setEvolutionEffectsVisible(isVisible: boolean, duration = 3000) {
    if (isVisible) {
      await waitForAssets(petEvolutionAssets);
      this.glowClip.play('evolution_vfx_anims_final');

      const { screenSize } = StateObserver.getState().ui;
      this.glowBackground = this.getSuperview().addSubview(
        new View({
          tag: 'evolutionGlowBackground',
          ...uiConfig.popups.backgroundDarker,
          width: screenSize.width,
          height: screenSize.height,
        }),
        this,
      );
    }

    await this.glowEffectAnimator.setTarget(isVisible ? 1 : 0, duration);

    if (!isVisible) {
      this.glowBackground?.removeFromSuperview();
    }
  }
}

const petEntityOpts: Record<PetSpecies, { url: string; scale: number }> = {
  ball: {
    url: 'assets/species/ball',
    scale: 0.79,
  },
  crab: {
    url: 'assets/species/crab',
    scale: 0.79,
  },
  dog: {
    url: 'assets/species/dog',
    scale: 0.79,
  },
  starball: {
    url: 'assets/species/starball',
    scale: 0.79,
  },
  hamster: {
    url: 'assets/species/hamster',
    scale: 0.79,
  },
  turtle: {
    url: 'assets/species/turtle',
    scale: 0.79,
  },
  seaotter: {
    url: 'assets/species/seaotter',
    scale: 0.79,
  },
  cat: {
    url: 'assets/species/cat',
    scale: 0.79,
  },
  whale: {
    url: 'assets/species/whale',
    scale: 0.79,
  },
  giraffe: {
    url: 'assets/species/giraffe',
    scale: 0.79,
  },
};

export class CareItemEntity extends MovieClip implements Entity {
  private static readonly clips: Record<CareItemType, string> = {
    food: 'assets/care/food',
    snack: 'assets/care/snack',
    toy: 'assets/care/toy',
    first_aid: 'assets/care/firstAid',
    invitation: 'assets/care/invitation',
  };

  public moving = false;

  constructor(public entityId: string, item: CareItem) {
    super({
      url: CareItemEntity.clips[item.type],
      width: 100,
      offsetX: -50,
      offsetY: 50,
    });

    this.play(getCareItemAnimationName(item.id));
    this.pause();
  }

  setFlipped(flipped: boolean) {
    this.updateOpts({ flipX: flipped });
  }
}

const entities: Record<string, Entity> = {};

/* eslint-disable no-redeclare */
function getEntityId(type: 'pet', playerId: string);
function getEntityId(
  type: 'careItem',
  careItemId: CareItem['id'],
  playerId: string,
);
function getEntityId(type: EntityType, id: string, playerId?: string) {
  return `${type}-${id}${playerId ? `-${playerId}` : ''}`;
}

export const fromEntityId = (entityId: string) => {
  let [type, id, ...idParts] = entityId.split('-');
  const playerId = idParts?.join('-');
  if (type !== 'careItem' && playerId) {
    id += `-${playerId}`;
  }

  return {
    type,
    id,
    playerId,
  };
};

function getEntity(entityId: string) {
  return entities[entityId];
}

function createOrGetEntity(entityId: string) {
  const { type, id } = fromEntityId(entityId);
  if (!entities[entityId]) {
    switch (type) {
      case 'pet': {
        const { user } = StateObserver.getState();
        const evolution = Pet.getters(user).getCurrentEvolution();
        entities[entityId] = new PetEntity(entityId, evolution?.species);
        break;
      }

      case 'careItem': {
        const careItem = Care.ruleset.careItems[id];
        entities[entityId] = new CareItemEntity(entityId, careItem);
        entities[entityId].on('ViewRemoved', () => delete entities[entityId]);

        break;
      }
    }
  }

  return getEntity(entityId);
}

export const EntityManager = {
  getEntityId,

  createOrGetEntity,

  getEntity,

  createOrGetPlayerPet: () =>
    createOrGetEntity(getEntityId('pet', platform.playerID)) as PetEntity,

  createOrGetFriendsPet: (friend: string) =>
    createOrGetEntity(getEntityId('pet', friend)) as PetEntity,

  deleteEntity: (entityId: string) => {
    entities[entityId]?.removeFromSuperview();
    delete entities[entityId];
  },
};
